import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { money, timeAgo } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Plus, Trash2, TrendingDown, AlertTriangle } from "lucide-react";

const REASONS = ["spoilage", "expired", "breakage", "over-prep", "theft", "other"];
const COLORS = ["#FF6414", "#FF3B30", "#FFCC00", "#34C759", "#0071E3", "#AF52DE"];

export default function Waste() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ inventory_id: "", qty: "", reason: "spoilage", note: "" });

  const { data } = useQuery({ queryKey: ["waste"], queryFn: async () => (await api.get("/waste?days=30")).data });
  const { data: inventory = [] } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const sym = settings?.currency_symbol || "₹";

  const log = async () => {
    if (!form.inventory_id || !form.qty) return toast.error("Select item & quantity");
    await api.post("/waste", { ...form, qty: Number(form.qty) });
    toast.success("Wastage logged & stock adjusted"); setOpen(false);
    setForm({ inventory_id: "", qty: "", reason: "spoilage", note: "" });
    qc.invalidateQueries({ queryKey: ["waste"] }); qc.invalidateQueries({ queryKey: ["inventory"] });
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Waste & Spoilage" subtitle="Track where inventory money is leaking every week">
        <Button onClick={() => setOpen(true)} data-testid="add-waste-btn" className="bg-[var(--kul-primary)] text-white"><Plus className="w-4 h-4 mr-1" /> Log Wastage</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="kul-card p-5" data-testid="waste-week">
          <div className="text-xs uppercase tracking-wide text-neutral-500 font-semibold flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5 text-[var(--kul-danger)]" /> This Week</div>
          <div className="mt-2 font-display text-2xl lg:text-3xl font-700 text-[var(--kul-danger)]">{money(data?.week_cost || 0, sym)}</div>
        </div>
        <div className="kul-card p-5">
          <div className="text-xs uppercase tracking-wide text-neutral-500 font-semibold">Last 30 days</div>
          <div className="mt-2 font-display text-2xl lg:text-3xl font-700">{money(data?.total_cost || 0, sym)}</div>
        </div>
        <div className="lg:col-span-2 kul-card p-5">
          <div className="text-xs uppercase tracking-wide text-neutral-500 font-semibold mb-1">Loss by Reason</div>
          <ResponsiveContainer width="100%" height={90}>
            <PieChart>
              <Pie data={data?.by_reason || []} dataKey="value" nameKey="reason" cx="50%" cy="50%" innerRadius={22} outerRadius={40}>
                {(data?.by_reason || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => money(v, sym)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {data?.top_items?.length > 0 && (
        <div className="kul-card p-5 mb-4">
          <h3 className="font-display font-700 mb-3">Biggest Money Leaks</h3>
          <div className="space-y-2">
            {data.top_items.map((it, i) => (
              <div key={it.name} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-md bg-neutral-100 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span className="flex-1 text-sm">{it.name}</span>
                <div className="w-40 h-2 rounded-full bg-neutral-100 overflow-hidden">
                  <div className="h-full bg-[var(--kul-danger)]" style={{ width: `${(it.value / data.top_items[0].value) * 100}%` }} />
                </div>
                <span className="w-20 text-right font-mono-k text-[var(--kul-danger)]">{money(it.value, sym)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="kul-card overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-neutral-50"><TableHead>Item</TableHead><TableHead>Qty</TableHead><TableHead>Reason</TableHead><TableHead className="text-right">Cost</TableHead><TableHead>When</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
          <TableBody>
            {(data?.logs || []).map((l) => (
              <TableRow key={l.id} data-testid={`waste-row-${l.id}`}>
                <TableCell className="font-medium">{l.name}</TableCell>
                <TableCell className="font-mono-k">{l.qty} {l.unit}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{l.reason}</Badge>{l.note ? <span className="text-xs text-neutral-400 ml-2">{l.note}</span> : ""}</TableCell>
                <TableCell className="text-right font-mono-k text-[var(--kul-danger)]">{money(l.cost, sym)}</TableCell>
                <TableCell className="text-sm text-neutral-400">{timeAgo(l.created_at)}</TableCell>
                <TableCell className="text-sm text-neutral-400">{l.created_by}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!data?.logs?.length && <p className="text-neutral-400 text-center py-12">No wastage logged. That's a good thing! 🎉</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Log Wastage</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Ingredient</Label>
              <select value={form.inventory_id} onChange={(e) => setForm({ ...form, inventory_id: e.target.value })} className="w-full h-10 rounded-xl border px-2 text-sm" data-testid="waste-item">
                <option value="">Select…</option>
                {inventory.map((i) => <option key={i.id} value={i.id}>{i.name} ({i.stock} {i.unit} left)</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Quantity wasted</Label><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="waste-qty" /></div>
              <div><Label>Reason</Label><select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full h-10 rounded-xl border px-2 text-sm capitalize" data-testid="waste-reason">{REASONS.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
            </div>
            <div><Label>Note (optional)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={log} className="bg-[var(--kul-primary)] text-white" data-testid="save-waste">Log Waste</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
